import { apiClient, unwrap, type ApiSuccess } from '@/lib/api-client';
import type {
  ImportJobDto,
  ImportType,
  PromoteResult,
  SourceFileDto,
  SourceFileType,
  StagingRowDto,
  StagingRowListMeta,
  StagingRowListParams,
  StagingRowListResult,
} from './types';

/**
 * API wrappers for Source Files / Import Jobs / Staging Rows.
 * Endpoints from docs/specs/06-ui-api-spec.md §5.
 */

export async function listSourceFiles(batchId: string): Promise<SourceFileDto[]> {
  return unwrap(
    apiClient.get<ApiSuccess<SourceFileDto[]>>(`/monthly-batches/${batchId}/source-files`),
  );
}

export interface UploadSourceFileInput {
  batchId: string;
  file: File;
  sourceFileType: SourceFileType;
}

export async function uploadSourceFile({
  batchId,
  file,
  sourceFileType,
}: UploadSourceFileInput): Promise<SourceFileDto> {
  const form = new FormData();
  form.append('file', file);
  form.append('source_file_type', sourceFileType);
  return unwrap(
    apiClient.post<ApiSuccess<SourceFileDto>>(`/monthly-batches/${batchId}/source-files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  );
}

export interface AttachSourceFileFromLibraryInput {
  batchId: string;
  companyFileId: string;
  sourceFileType: SourceFileType;
}

export async function attachSourceFileFromLibrary({
  batchId,
  companyFileId,
  sourceFileType,
}: AttachSourceFileFromLibraryInput): Promise<SourceFileDto> {
  return unwrap(
    apiClient.post<ApiSuccess<SourceFileDto>>(
      `/monthly-batches/${batchId}/source-files/from-library`,
      {
        company_file_id: companyFileId,
        source_file_type: sourceFileType,
      },
    ),
  );
}

export interface StartImportJobInput {
  fileId: string;
  importType: ImportType;
}

export async function startImportJob({
  fileId,
  importType,
}: StartImportJobInput): Promise<ImportJobDto> {
  return unwrap(
    apiClient.post<ApiSuccess<ImportJobDto>>(`/source-files/${fileId}/import-jobs`, {
      import_type: importType,
    }),
  );
}

export async function getImportJob(jobId: string): Promise<ImportJobDto> {
  return unwrap(apiClient.get<ApiSuccess<ImportJobDto>>(`/import-jobs/${jobId}`));
}

export async function listStagingRows(
  jobId: string,
  params: StagingRowListParams = {},
): Promise<StagingRowListResult> {
  const res = await apiClient.get<ApiSuccess<StagingRowDto[], StagingRowListMeta>>(
    `/import-jobs/${jobId}/staging-rows`,
    { params },
  );
  return {
    rows: res.data.data,
    meta: res.data.meta ?? {
      total: res.data.data.length,
      page: 1,
      per_page: res.data.data.length,
    },
  };
}

export interface PatchStagingRowInput {
  rowId: string;
  normalizedPayload: Record<string, unknown>;
  correctionReason: string;
  status?: 'corrected' | 'ignored';
}

export async function patchStagingRow({
  rowId,
  normalizedPayload,
  correctionReason,
  status,
}: PatchStagingRowInput): Promise<StagingRowDto> {
  const body: Record<string, unknown> = {
    normalized_payload: normalizedPayload,
    correction_reason: correctionReason,
  };
  if (status) body.status = status;
  return unwrap(apiClient.patch<ApiSuccess<StagingRowDto>>(`/import-staging-rows/${rowId}`, body));
}

/**
 * Promote endpoint (P1-#8): backend returns the post-promotion
 * `ImportJobDto`; callers can read the summary keys for the row counts.
 */
export async function promoteImportJob(jobId: string): Promise<ImportJobDto> {
  return unwrap(apiClient.post<ApiSuccess<ImportJobDto>>(`/import-jobs/${jobId}/promote`, {}));
}

/** Derive the FE-facing promote result counts from a job DTO. */
export function promoteResultFromJob(job: ImportJobDto): PromoteResult {
  const s = job.summary ?? {};
  return {
    inbound_promoted: s.inbound_promoted,
    dismantling_promoted: s.dismantling_promoted,
    promoted_at: s.promoted_at,
  };
}
